import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { updateCampaignStatus } from '../utils/campaign';
import {
  toastSuccessConfig,
  toastErrorConfig,
} from '../components/shared/constants/toaster-config';

const SUCCESS_MESSAGES = {
  PAUSED: 'Campaña pausada',
  CANCELED: 'Campaña cancelada',
  SCHEDULED: 'Campaña reanudada',
};

const RESCHEDULE_SUCCESS_MESSAGE = 'Campaña reprogramada';

const ERROR_CODE_MESSAGES = {
  INVALID_STATUS: 'Transición de estado no permitida',
  BROADCAST_ALREADY_FINISHED: 'La campaña ya finalizó',
  BROADCAST_WAS_CANCELED: 'La campaña fue cancelada',
  BROADCAST_NOT_PAUSED: 'La campaña no está pausada',
  RESUME_DATE_IN_PAST: 'La fecha de reanudación es en el pasado',
  CAMPAIGN_NOT_FOUND: 'No se encontró la campaña',
};

const UNEXPECTED_CODES = new Set(['SERVER_ERROR']);

function resolveErrorMessage(error) {
  // The axios interceptor sets message = data?.userMessage || data.
  // For backend responses shaped as { error: CODE }, message is the full
  // response data object { error: CODE }.
  const code =
    (error.message && typeof error.message === 'object' && error.message.error) ||
    error.internalCode ||
    null;

  if (code && ERROR_CODE_MESSAGES[code]) {
    return { message: ERROR_CODE_MESSAGES[code], isExpected: true };
  }

  if (code && UNEXPECTED_CODES.has(code)) {
    return { message: 'Ocurrió un error en el servidor. Intenta nuevamente.', isExpected: false };
  }

  return { message: 'Ocurrió un error inesperado. Intenta nuevamente.', isExpected: false };
}

/**
 * Hook that exposes campaign status transition actions (pause, cancel, resume).
 * @param {Object} options
 * @param {Function} options.onSuccess - Callback invoked with the new status string after a successful transition.
 * @returns {{ changeStatus: Function, isUpdating: boolean }}
 */
export const useCampaignStatusActions = ({ onSuccess } = {}) => {
  const [isUpdating, setIsUpdating] = useState(false);

  /**
   * Transitions a campaign to a new status.
   * @param {string} status - Target status (e.g. 'SCHEDULED', 'PAUSED', 'CANCELED').
   * @param {string} campaignId - Campaign identifier.
   * @param {Object} [options={}]
   * @param {string} [options.sendAt] - Optional ISO-like datetime string ("YYYY-MM-DDTHH:mm:ss") to
   *   anchor the first pending batch when resuming. No timezone suffix — server treats it as local time.
   * @returns {Promise<boolean>} true if the transition succeeded, false otherwise.
   */
  const changeStatus = useCallback(async (status, campaignId, options = {}) => {
    const { sendAt } = options;
    setIsUpdating(true);
    try {
      await updateCampaignStatus(campaignId, status, sendAt);
      const successMessage =
        status === 'SCHEDULED' && sendAt
          ? RESCHEDULE_SUCCESS_MESSAGE
          : SUCCESS_MESSAGES[status] || 'Estado actualizado';
      toast.success(successMessage, toastSuccessConfig);
      if (onSuccess) {
        onSuccess(status);
      }
      return true;
    } catch (error) {
      const { message, isExpected } = resolveErrorMessage(error);
      toast.error(message, toastErrorConfig);
      if (!isExpected) {
        Sentry.captureException(error, {
          tags: { component: 'BroadcastAnalitycs', op: 'updateCampaignStatus' },
        });
      }
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [onSuccess]);

  return { changeStatus, isUpdating };
};

export default useCampaignStatusActions;
